/**
 * AI Tutor Service for German Learning Platform - Level B2 Dedicated
 * Mentored by Maximilian AI (B2 Goethe / telc Specialist)
 * Supports OpenAI API with key resolution from Supabase, .env, or localStorage.
 */

import { supabase } from '../lib/supabaseClient';

const STORAGE_KEY_OPENAI_KEY = 'openai_api_key';
const STORAGE_KEY_OPENAI_MODEL = 'openai_model_choice';
const STORAGE_KEY_TUTOR_HISTORY = 'ai_tutor_chat_history_b2';

export const DEFAULT_MODEL = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';
export const TUTOR_LEVEL = 'B2';

let cachedSupabaseKey = null;

/**
 * Attempts to retrieve OpenAI API Key from Supabase.
 * Checks table `app_settings` (key: 'openai_api_key' or 'OPENAI_API_KEY')
 * or table `secrets` (name: 'openai_api_key').
 */
export const fetchApiKeyFromSupabase = async () => {
  if (cachedSupabaseKey) return cachedSupabaseKey;

  try {
    // 1. Try `app_settings` table (standard platform settings pattern)
    const { data: settingData, error: settingError } = await supabase
      .from('app_settings')
      .select('value')
      .in('key', ['openai_api_key', 'OPENAI_API_KEY', 'openai_key'])
      .limit(1)
      .maybeSingle();

    if (!settingError && settingData?.value) {
      cachedSupabaseKey = settingData.value.trim();
      return cachedSupabaseKey;
    }

    // 2. Try `secrets` table
    const { data: secretData, error: secretError } = await supabase
      .from('secrets')
      .select('value')
      .in('name', ['openai_api_key', 'OPENAI_API_KEY'])
      .limit(1)
      .maybeSingle();

    if (!secretError && secretData?.value) {
      cachedSupabaseKey = secretData.value.trim();
      return cachedSupabaseKey;
    }
  } catch (err) {
    // Silently fall through if table doesn't exist yet
    console.debug('Supabase API key lookup note:', err?.message);
  }

  return null;
};

/**
 * Resolves the active API key with priority:
 * 1. User passed key (temporary / test)
 * 2. Environment variable REACT_APP_OPENAI_API_KEY
 * 3. Supabase stored key (`app_settings` or `secrets` table)
 * 4. Client localStorage key
 */
export const getActiveApiKey = async (explicitKey = null) => {
  if (explicitKey && explicitKey.trim()) {
    return explicitKey.trim();
  }

  if (process.env.REACT_APP_OPENAI_API_KEY && process.env.REACT_APP_OPENAI_API_KEY.trim()) {
    return process.env.REACT_APP_OPENAI_API_KEY.trim();
  }

  const supabaseKey = await fetchApiKeyFromSupabase();
  if (supabaseKey) {
    return supabaseKey;
  }

  const localKey = localStorage.getItem(STORAGE_KEY_OPENAI_KEY);
  if (localKey && localKey.trim()) {
    return localKey.trim();
  }

  return '';
};

export const getSavedApiKey = () => {
  return (
    process.env.REACT_APP_OPENAI_API_KEY ||
    cachedSupabaseKey ||
    localStorage.getItem(STORAGE_KEY_OPENAI_KEY) ||
    ''
  );
};

export const getKeySource = () => {
  if (process.env.REACT_APP_OPENAI_API_KEY && process.env.REACT_APP_OPENAI_API_KEY.trim()) return 'env';
  if (cachedSupabaseKey) return 'supabase';
  if (localStorage.getItem(STORAGE_KEY_OPENAI_KEY)) return 'local';
  return 'none';
};

export const saveApiKey = (key) => {
  if (!key) {
    localStorage.removeItem(STORAGE_KEY_OPENAI_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY_OPENAI_KEY, key.trim());
  }
};

export const getSavedModel = () => {
  return localStorage.getItem(STORAGE_KEY_OPENAI_MODEL) || DEFAULT_MODEL;
};

export const saveModel = (model) => {
  localStorage.setItem(STORAGE_KEY_OPENAI_MODEL, model);
};

export const getStoredChatHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TUTOR_HISTORY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveStoredChatHistory = (history) => {
  try {
    // Keep last 30 messages to prevent storage bloat
    const trimmed = (history || []).slice(-30);
    localStorage.setItem(STORAGE_KEY_TUTOR_HISTORY, JSON.stringify(trimmed));
  } catch (err) {
    console.error('Failed to save chat history:', err);
  }
};

export const clearStoredChatHistory = () => {
  localStorage.removeItem(STORAGE_KEY_TUTOR_HISTORY);
};

/**
 * Builds the specialized B2 Goethe / telc Tutor System Prompt for Maximilian AI.
 */
const getB2TutorSystemPrompt = () => {
  return `You are "Maximilian", an elite, sophisticated German language professor and B2 exam mentor (Goethe-Zertifikat B2 & telc Deutsch B2) for Albanian-speaking students.

ROLE & PERSONA:
- Name: Maximilian
- Tone: Professional, encouraging, intellectually rigorous, culturally refined.
- Target Level: Exclusively CEFR B2 (Upper-Intermediate to Advanced).

LINGUISTIC EXPECTATIONS FOR LEVEL B2:
1. German Output:
   - Use natural, grammatically rich German (Passiv, Konjunktiv II, Partizipialkonstruktionen, Nebensätze mit 'obwohl', 'indem', 'sodass', 'während', 'je... desto...').
   - Include authentic B2 Redemittel (e.g., "Meiner Ansicht nach...", "Es lässt sich nicht leugnen, dass...", "Ein wesentlicher Aspekt dabei ist...").
   - Use high-level Nomen-Verb-Verbindungen (e.g., "in Betracht ziehen", "zur Verfügung stehen", "eine Entscheidung treffen").

2. Analysis & Albanian Error Correction:
   - Scrutinize the user's German for case precision (Akkusativ/Dativ/Genitiv), adjective endings, verb placement (Nebensatz verb-final rule), preposition choice, and register (Sie vs. du).
   - If there is an error or a stylistically clumsy phrase, provide a clear, constructive correction and EXPLAIN THE GRAMMAR RULE IN ALBANIAN (shqip).
   - If the user wrote without errors, praise their formulation and suggest an even more idiomatic B2 alternative or ask an engaging B2 debate question.

3. Albanian Queries:
   - If the student asks questions in Albanian (e.g. grammar rules, exam tips, translation differences), answer clearly and pedagogical in Albanian with rich German B2 examples.

RESPONSE FORMAT:
Always return a valid JSON object with the exact fields:
{
  "reply": "Your primary conversational response in B2 German (2-4 sophisticated sentences).",
  "correction": "Null if no notable errors. If there is a mistake or better B2 formulation, explain WHY in Albanian: \\"💡 Korrigjim B2: '...' (jo '...'). Në gjermanisht...\\"",
  "newWords": [
    { "de": "die Nomen-Verb-Verbindung", "sq": "bashkimi emër-folje" }
  ],
  "suggestedReplies": [
    "Suggested German response 1 (B2 level)",
    "Suggested German response 2 (B2 level)"
  ]
}

DO NOT wrap the response in markdown backticks or any other text. Return ONLY the raw JSON object.`;
};

/**
 * Specialized B2 Demo simulation when no API key is set yet.
 */
const generateB2DemoTutorResponse = (userMessage) => {
  const lower = userMessage.toLowerCase();

  if (lower.includes('hallo') || lower.includes('përshëndetje') || lower.includes('hi') || lower.includes('mirmëngjes')) {
    return {
      reply: `Guten Tag! Ich freue mich außerordentlich, Sie auf Ihrem Weg zum B2-Zertifikat begleiten zu dürfen. Über welches anspruchsvolle Thema möchten Sie sich heute austauschen – Beruf, Umwelt oder moderne Medien?`,
      correction: null,
      newWords: [
        { de: 'begleiten zu dürfen', sq: 'të kem nderin t\'ju shoqëroj' },
        { de: 'sich austauschen über (+ Akk.)', sq: 'shkëmbej mendime rreth' },
      ],
      suggestedReplies: [
        'Ich möchte über die Digitalisierung der Arbeitswelt diskutieren.',
        'Können wir eine B2-Bildbeschreibung üben?',
        'Erkläre mir bitte die Passiversatzformen.',
      ],
    };
  }

  if (lower.includes('passiv') || lower.includes('ersatzform') || lower.includes('lassen')) {
    return {
      reply: `Ein hervorragendes B2-Grammatikthema! Im B2-Niveau ersetzen wir das Passiv häufig durch elegantere Strukturen, wie "sich lassen + Infinitiv" (z.B. "Das Problem lässt sich leicht lösen" anstelle von "Das Problem kann gelöst werden").`,
      correction: null,
      newWords: [
        { de: 'die Passiversatzform, -en', sq: 'forma zëvendësuese e pësore' },
        { de: 'sich lassen + Infinitiv', sq: 'mund të bëhet (mundësi pësore)' },
      ],
      suggestedReplies: [
        'Können Sie mir ein weiteres Beispiel geben?',
        'Was ist mit "sein + zu + Infinitiv"?',
      ],
    };
  }

  if (lower.includes('zu hause') && (lower.includes('gehe') || lower.includes('fahre') || lower.includes('komme'))) {
    return {
      reply: `Das ist nachvollziehbar, denn eine ausgewogene Work-Life-Balance ist für Berufstätige unerlässlich. Was haben Sie für heute Abend noch vorgesehen?`,
      correction: `💡 Korrigjim B2: Kur shprehim lëvizje drejtimi (gehen, fahren, ecur), përdorim lokucionin "nach Hause" dhe jo "zu Hause". P.sh.: "Ich fahre nach Hause". "Zu Hause" përdoret ekskluzivisht për gjendje statike pa lëvizje ("Ich bin zu Hause").`,
      newWords: [
        { de: 'nachvollziehbar', sq: 'e kuptueshme / e logjikshme' },
        { de: 'vorgesehen haben', sq: 'kam planifikuar / parashikuar' },
      ],
      suggestedReplies: [
        'Ich beabsichtige, mich auf die B2-Prüfung vorzubereiten.',
        'Ich möchte einen Vortrag für morgen vorbereiten.',
      ],
    };
  }

  // Generic B2 encouraging response
  return {
    reply: `Ihr Gedankengang ist schlüssig und entspricht gut den Anforderungen des B2-Niveaus. Lassen Sie uns dieses Argument noch präziser untermauern: Welche Gegenargumente könnten in dieser Debatte eine Rolle spielen?`,
    correction: null,
    newWords: [
      { de: 'schlüssig', sq: 'koherent / bindës' },
      { de: 'ein Argument untermauern', sq: 'mbështes / forcoj një argument' },
    ],
    suggestedReplies: [
      'Einerseits spricht dafür, dass...',
      'Andererseits darf man nicht übersehen, dass...',
      'Zusammenfassend lässt sich sagen, dass...',
    ],
  };
};

/**
 * Sends a conversation message to OpenAI with B2 specialization.
 */
export const sendTutorMessage = async ({
  messages,
  apiKey = null,
  model = DEFAULT_MODEL,
}) => {
  const activeKey = await getActiveApiKey(apiKey);

  // If no OpenAI API key is supplied, run the B2 pedagogical simulation
  if (!activeKey) {
    await new Promise((res) => setTimeout(res, 650));
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
    const demoData = generateB2DemoTutorResponse(lastUserMsg);
    return {
      ...demoData,
      isDemo: true,
    };
  }

  const systemMessage = {
    role: 'system',
    content: getB2TutorSystemPrompt(),
  };

  // Convert messages to standard OpenAI format
  const apiMessages = [
    systemMessage,
    ...messages.slice(-10).map((m) => ({
      role: m.role === 'tutor' || m.role === 'assistant' ? 'assistant' : 'user',
      content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
    })),
  ];

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeKey}`,
      },
      body: JSON.stringify({
        model: model || DEFAULT_MODEL,
        messages: apiMessages,
        temperature: 0.7,
        max_tokens: 650,
        response_format: { type: 'json_object' },
      }),
    });

    if (!response.ok) {
      const errorJson = await response.json().catch(() => ({}));
      const errorMsg =
        errorJson?.error?.message || `Gabim nga OpenAI API (${response.status})`;
      throw new Error(errorMsg);
    }

    const data = await response.json();
    const rawContent = data.choices?.[0]?.message?.content;

    if (!rawContent) {
      throw new Error('Përgjigjja nga OpenAI ishte bosh.');
    }

    const parsed = JSON.parse(rawContent);
    return {
      reply: parsed.reply || 'Sehr gut formuliert!',
      correction: parsed.correction || null,
      newWords: Array.isArray(parsed.newWords) ? parsed.newWords : [],
      suggestedReplies: Array.isArray(parsed.suggestedReplies) ? parsed.suggestedReplies : [],
      isDemo: false,
    };
  } catch (err) {
    console.error('OpenAI B2 tutor request failed:', err);
    throw err;
  }
};
